// Support 页 Tab 面板切换：激活 Tab 才显示对应 Section

class SupportTabs extends HTMLElement {
  constructor() {
    super();
    this.items = [];
    this.currentIndex = 0;
    this._isDesignMode = Boolean(window.Shopify && window.Shopify.designMode);
    this._itemClickHandlers = [];
    this._onHashChange = this._handleHashChange.bind(this);
    this._onBlockSelect = this._handleBlockSelect.bind(this);
    this._onSectionSelect = this._handleSectionSelect.bind(this);
  }

  connectedCallback() {
    this.items = Array.from(this.querySelectorAll(".support-tabs__item")).map((element) => ({
      element,
      targetId: element.getAttribute("data-target-id") || "",
      targetElement: null,
      sectionWrapper: null,
    }));

    this.items.forEach((item) => {
      if (!item.targetId) return;
      item.targetElement = document.getElementById(item.targetId);
      item.sectionWrapper = item.targetElement ? item.targetElement.closest(".shopify-section") : null;

      if (item.sectionWrapper) {
        item.sectionWrapper.classList.add("support-tab-panel");
        item.targetElement.setAttribute("role", "tabpanel");
      }
    });

    this._bindEvents();

    const initialIndex = this._getInitialIndex();
    this._setActive(initialIndex, { updateHash: false, instant: true });
  }

  disconnectedCallback() {
    this._itemClickHandlers.forEach(({ element, handler }) => {
      element.removeEventListener("click", handler);
    });
    this._itemClickHandlers = [];

    if (this._isDesignMode) {
      document.removeEventListener("shopify:block:select", this._onBlockSelect);
      document.removeEventListener("shopify:section:select", this._onSectionSelect);
      return;
    }

    window.removeEventListener("hashchange", this._onHashChange);
  }

  _bindEvents() {
    this._itemClickHandlers = [];

    this.items.forEach((item, index) => {
      item.element.setAttribute("role", "tab");
      item.element.setAttribute("data-support-tab-index", index);
      const clickHandler = (e) => {
        e.preventDefault();
        this._setActive(index);
      };
      item.element.addEventListener("click", clickHandler);
      this._itemClickHandlers.push({ element: item.element, handler: clickHandler });
    });

    if (this._isDesignMode) {
      document.addEventListener("shopify:block:select", this._onBlockSelect);
      document.addEventListener("shopify:section:select", this._onSectionSelect);
      return;
    }

    window.addEventListener("hashchange", this._onHashChange);
  }

  _handleBlockSelect(e) {
    if (e.target.hasAttribute("data-support-tab-index")) {
      const index = e.target.getAttribute("data-support-tab-index");
      this._setActive(+index, { updateHash: false });
    }
  }

  _handleSectionSelect(e) {
    this.items.forEach((item, index) => {
      if (e.target.querySelector(`#${item.targetId}`)) {
        this._setActive(index, { updateHash: false });
      }
    });
  }

  _handleHashChange() {
    const index = this._getInitialIndex();
    if (index !== this.currentIndex) {
      this._setActive(index, { updateHash: false });
    }
  }

  _getInitialIndex() {
    const hash = window.location.hash.replace(/^#/, "");
    if (hash) {
      const matchedIndex = this.items.findIndex((item) => item.targetId === hash);
      if (matchedIndex >= 0) return matchedIndex;
    }
    return 0;
  }

  _setActive(index, options = {}) {
    const { updateHash = true, instant = false } = options;
    const activeItem = this.items[index];
    if (!activeItem) return;

    this.currentIndex = index;

    this.items.forEach((item, i) => {
      const isActive = i === index;

      item.element.classList.toggle("is-active", isActive);
      item.element.setAttribute("aria-selected", String(isActive));
      if (isActive) {
        item.element.setAttribute("aria-current", "true");
      } else {
        item.element.removeAttribute("aria-current");
      }

      if (!item.sectionWrapper) return;

      item.sectionWrapper.classList.toggle("is-active", isActive);
      item.sectionWrapper.hidden = !isActive;

      if (item.targetElement) {
        item.targetElement.setAttribute("aria-hidden", String(!isActive));
      }
    });

    if (updateHash && activeItem.targetId) {
      const nextHash = `#${activeItem.targetId}`;
      if (window.location.hash !== nextHash) {
        history.replaceState(null, "", nextHash);
      }
    }

    this._scrollActiveIntoView({ instant });
  }

  /** 移动端横向可滚动时，把激活项滚动到居中可见（滚动容器为 support-tabs 自身） */
  _scrollActiveIntoView(options = {}) {
    const { instant = false } = options;
    const item = this.items[this.currentIndex];
    if (!item) return;

    const host = this;
    if (host.scrollWidth <= host.clientWidth) return;

    const hostRect = host.getBoundingClientRect();
    const itemRect = item.element.getBoundingClientRect();
    const itemCenter = itemRect.left - hostRect.left + itemRect.width / 2;
    const targetScrollLeft = host.scrollLeft + itemCenter - host.clientWidth / 2;
    const maxScroll = host.scrollWidth - host.clientWidth;

    host.scrollTo({
      left: Math.max(0, Math.min(targetScrollLeft, maxScroll)),
      behavior: instant ? "auto" : "smooth",
    });
  }
}

if (!window.customElements.get("support-tabs")) {
  window.customElements.define("support-tabs", SupportTabs);
}
